import { usePipelineDetail, usePipelines } from '@/features/board/useBoard'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { useTemplates } from '@/features/messaging/useMessaging'
import { useMembers } from '@/features/team/useTeam'
import type { Lookups } from '@/features/workflows/workflowMeta'
import type { Stage } from '@/api/types'

/** Names for the ids a workflow stores (stages, templates, people, date fields) plus the option lists. */
export function useWorkflowLookups() {
  const { data: pipelines } = usePipelines()
  const pipeline = pipelines?.find((p) => p.isDefault) ?? pipelines?.[0]
  const { data: detail } = usePipelineDetail(pipeline?.id)
  const stages: Stage[] = detail ? [...detail.sections.flatMap((s) => s.stages), ...detail.looseStages] : []
  const { data: templates } = useTemplates()
  const { data: members } = useMembers()
  const { data: dealFields } = useFieldDefinitions('deal')
  const { data: contactFields } = useFieldDefinitions('contact')

  const dealDateFields = [
    { value: 'next_contact_date', label: 'Next contact (next task due)' },
    ...(dealFields ?? []).filter((f) => f.dataType === 'date').map((f) => ({ value: f.fieldKey, label: f.label })),
  ]
  const contactDateFields = [
    { value: 'birthday', label: 'Birthday' },
    { value: 'client_since', label: 'Client since (date added)' },
    ...(contactFields ?? []).filter((f) => f.dataType === 'date').map((f) => ({ value: f.fieldKey, label: f.label })),
  ]

  const lookups: Lookups = {
    stageName: (id) => stages.find((s) => s.id === id)?.name ?? 'a stage',
    templateName: (id) => templates?.find((t) => t.id === id)?.name ?? 'a template',
    memberName: (id) => members?.find((m) => m.id === id)?.fullName ?? 'a teammate',
    dateFieldLabel: (raw) => {
      const key = raw.startsWith('custom.') ? raw.slice('custom.'.length) : raw
      return ([...dealDateFields, ...contactDateFields].find((o) => o.value === key)?.label ?? key).toLowerCase()
    },
  }
  return { lookups, stages, templates: templates ?? [], members: members ?? [], dealDateFields, contactDateFields }
}
